import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Save, Calculator, AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent } from '@/components/ui/card';

// Normaliza un tipo de servicio para comparaciones robustas
// (sin mayúsculas ni acentos). Así "Agua Caliente", "Agua caliente"
// y "agua caliente" se tratan igual.
const normalizar = (valor) =>
  String(valor || '')
    .trim()
    .toLowerCase()
    .replace(/[áàä]/g, 'a')
    .replace(/[éèë]/g, 'e')
    .replace(/[íìï]/g, 'i')
    .replace(/[óòö]/g, 'o')
    .replace(/[úùü]/g, 'u');

const esTipoAgua = (tipo) => {
  const t = normalizar(tipo);
  return t === 'agua' || t === 'agua caliente';
};

// Resuelve el factor de conversión a litros para un servicio.
// 1) Usa el factorConversion configurado si es válido y distinto de 1.
// 2) Si no, lo deduce por tipo + unidad (m³ = 1000 L, galón = 3.78541 L).
// 3) En cualquier otro caso el factor es 1 (la unidad ya es litros o
//    no aplica conversión, p. ej. kwh).
const resolverFactorConversion = (servicio) => {
  const stored = parseFloat(servicio?.factorConversion);
  if (stored && stored > 0 && stored !== 1) return stored;

  const unidad = normalizar(servicio?.unidad);
  if (esTipoAgua(servicio?.tipo)) {
    if (unidad === 'm3') return 1000; // 1 m³ = 1000 litros
    if (unidad === 'galones') return 3.78541; // 1 galón = 3.78541 litros
  }
  return 1;
};

const EditUtilityServiceModal = ({ isOpen, onClose, onSave, item, isLoading }) => {
  const [formData, setFormData] = useState({
    fecha: '',
    contadorAnterior: '',
    contadorNuevo: '',
    consumo: 0,
    costo: 0,
    consumoLitros: 0,
  });

  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (item) {
      // Formatear la fecha para el input type="date"
      const fechaFormateada = item.fecha 
        ? new Date(item.fecha).toISOString().split('T')[0] 
        : '';

      setFormData({
        fecha: fechaFormateada,
        contadorAnterior: item.contadorAnterior || '',
        contadorNuevo: item.contadorNuevo || '',
        consumo: item.consumo || 0,
        costo: item.costo || 0,
        consumoLitros: item.consumoLitros || 0,
      });
    }
  }, [item]);

  // Calcular consumo y costo automáticamente cuando cambian los contadores
  useEffect(() => {
    const anterior = parseFloat(formData.contadorAnterior) || 0;
    const nuevo = parseFloat(formData.contadorNuevo) || 0;
    const precio = parseFloat(item?.servicio?.precio) || 0;

    // Factor de conversión a litros (robusto ante mayúsculas/acentos y
    // ante registros que no persistieron factorConversion).
    const factorConversion = resolverFactorConversion(item?.servicio);

    if (nuevo > anterior) {
      const consumoCalculado = nuevo - anterior;
      const costoCalculado = consumoCalculado * precio;
      const consumoLitrosCalculado = consumoCalculado * factorConversion;

      setFormData(prev => ({
        ...prev,
        consumo: consumoCalculado,
        costo: costoCalculado,
        consumoLitros: consumoLitrosCalculado,
      }));
    } else if (nuevo !== 0) {
      setFormData(prev => ({
        ...prev,
        consumo: 0,
        costo: 0,
        consumoLitros: 0,
      }));
    }
  }, [formData.contadorAnterior, formData.contadorNuevo, item?.servicio?.precio, item?.servicio?.factorConversion, item?.servicio?.tipo, item?.servicio?.unidad]);

  const handleChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));

    // Limpiar error del campo cuando el usuario empieza a escribir
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: null
      }));
    }
  };

  const validate = () => {
    const newErrors = {};

    if (!formData.fecha) {
      newErrors.fecha = 'La fecha es requerida';
    }

    if (!formData.contadorAnterior || formData.contadorAnterior === '') {
      newErrors.contadorAnterior = 'El contador anterior es requerido';
    }

    if (!formData.contadorNuevo || formData.contadorNuevo === '') {
      newErrors.contadorNuevo = 'El contador nuevo es requerido';
    }

    const anterior = parseFloat(formData.contadorAnterior) || 0;
    const nuevo = parseFloat(formData.contadorNuevo) || 0;

    if (nuevo <= anterior) {
      newErrors.contadorNuevo = 'El contador nuevo debe ser mayor que el anterior';
    }

    // Validar contra el límite de consumo diario si está configurado
    const consumoLimiteDia = item?.servicio?.consumoLimiteDia;
    if (consumoLimiteDia && formData.consumo > consumoLimiteDia) {
      newErrors.consumo = `El consumo (${formData.consumo.toFixed(2)}) supera el límite diario (${consumoLimiteDia})`;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!validate()) {
      return;
    }

    // Crear la fecha en formato ISO
    const [year, month, day] = formData.fecha.split('-');
    const recordDate = new Date(Date.UTC(parseInt(year), parseInt(month) - 1, parseInt(day), 12, 0, 0));

    const dataToSave = {
      fecha: recordDate.toISOString(),
      contadorAnterior: parseFloat(formData.contadorAnterior),
      contadorNuevo: parseFloat(formData.contadorNuevo),
      consumo: formData.consumo,
      costo: formData.costo,
      consumoLitros: formData.consumoLitros,
      servicio: item.servicio,
      lavanderia: item.lavanderia,
    };

    onSave(dataToSave);
  };

  if (!isOpen) return null;

  const exceedsLimit = item?.servicio?.consumoLimiteDia && formData.consumo > item.servicio.consumoLimiteDia;
  const nearLimit = item?.servicio?.consumoLimiteDia && formData.consumo > item.servicio.consumoLimiteDia * 0.8 && formData.consumo <= item.servicio.consumoLimiteDia;

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          {/* Overlay */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="absolute inset-0 bg-black/50"
            onClick={onClose}
          />

          {/* Modal */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            className="relative z-10 w-full max-w-2xl mx-4"
          >
            <Card className="shadow-2xl">
              <div className="flex items-center justify-between p-6 border-b">
                <div>
                  <h2 className="text-2xl font-bold">Editar Registro de Consumo</h2>
                  <p className="text-sm text-muted-foreground mt-1">
                    {item?.servicio?.nombre} - {item?.servicio?.tipo}
                  </p>
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={onClose}
                  disabled={isLoading}
                >
                  <X className="h-5 w-5" />
                </Button>
              </div>

              <form onSubmit={handleSubmit}>
                <CardContent className="p-6 space-y-6">
                  {/* Fecha */}
                  <div className="space-y-2">
                    <Label htmlFor="fecha">
                      Fecha del registro <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      id="fecha"
                      type="date"
                      value={formData.fecha}
                      onChange={(e) => handleChange('fecha', e.target.value)}
                      max={new Date().toISOString().split('T')[0]}
                      className={errors.fecha ? 'border-red-500' : ''}
                    />
                    {errors.fecha && (
                      <p className="text-sm text-red-500">{errors.fecha}</p>
                    )}
                  </div>

                  {/* Contadores */}
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="contadorAnterior">
                        Contador Anterior <span className="text-red-500">*</span>
                      </Label>
                      <Input
                        id="contadorAnterior"
                        type="number"
                        step="0.01"
                        value={formData.contadorAnterior}
                        onChange={(e) => handleChange('contadorAnterior', e.target.value)}
                        placeholder="0.00"
                        className={errors.contadorAnterior ? 'border-red-500' : ''}
                      />
                      {errors.contadorAnterior && (
                        <p className="text-sm text-red-500">{errors.contadorAnterior}</p>
                      )}
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="contadorNuevo">
                        Contador Nuevo <span className="text-red-500">*</span>
                      </Label>
                      <Input
                        id="contadorNuevo"
                        type="number"
                        step="0.01"
                        value={formData.contadorNuevo}
                        onChange={(e) => handleChange('contadorNuevo', e.target.value)}
                        placeholder="0.00"
                        className={errors.contadorNuevo ? 'border-red-500' : ''}
                      />
                      {errors.contadorNuevo && (
                        <p className="text-sm text-red-500">{errors.contadorNuevo}</p>
                      )}
                    </div>
                  </div>

                  {/* Información del servicio */}
                  <div className="grid grid-cols-2 gap-4 p-4 bg-muted rounded-lg">
                    <div>
                      <p className="text-sm text-muted-foreground">Unidad</p>
                      <p className="font-medium">{item?.servicio?.unidad || '-'}</p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Precio Unitario</p>
                      <p className="font-medium">${item?.servicio?.precio || 0}</p>
                    </div>
                  </div>

                  {/* Límite de consumo diario */}
                  {item?.servicio?.consumoLimiteDia && (
                    <div className="flex items-center gap-2 px-3 py-2 bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-800 rounded-md">
                      <AlertTriangle className="h-4 w-4 text-amber-600 dark:text-amber-500" />
                      <span className="text-xs text-amber-700 dark:text-amber-400 font-medium">
                        Límite diario: {item.servicio.consumoLimiteDia} {item.servicio.unidad}
                      </span>
                    </div>
                  )}

                  {/* Cálculos automáticos */}
                  {formData.contadorAnterior && formData.contadorNuevo && parseFloat(formData.contadorNuevo) > parseFloat(formData.contadorAnterior) && (
                    <div className={`p-4 rounded-lg space-y-3 ${
                      exceedsLimit ? 'bg-red-50 dark:bg-red-950/20 border border-red-200 dark:border-red-800' :
                      nearLimit ? 'bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-800' :
                      'bg-blue-50 dark:bg-blue-950/20 border border-blue-200 dark:border-blue-800'
                    }`}>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <Calculator className={`h-5 w-5 ${
                            exceedsLimit ? 'text-red-600' :
                            nearLimit ? 'text-amber-600' :
                            'text-blue-600'
                          }`} />
                          <span className="font-semibold">Cálculos Automáticos</span>
                        </div>
                        {exceedsLimit && (
                          <AlertTriangle className="h-5 w-5 text-red-600" />
                        )}
                      </div>

                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <p className="text-sm text-muted-foreground">Consumo</p>
                          <p className={`text-lg font-bold ${
                            exceedsLimit ? 'text-red-700 dark:text-red-400' :
                            nearLimit ? 'text-amber-700 dark:text-amber-400' :
                            'text-blue-700 dark:text-blue-400'
                          }`}>
                            {formData.consumo.toFixed(2)} {item?.servicio?.unidad}
                          </p>
                        </div>
                        <div>
                          <p className="text-sm text-muted-foreground">Costo Total</p>
                          <p className={`text-lg font-bold ${
                            exceedsLimit ? 'text-red-700 dark:text-red-400' :
                            nearLimit ? 'text-amber-700 dark:text-amber-400' :
                            'text-blue-700 dark:text-blue-400'
                          }`}>
                            ${formData.costo.toFixed(2)}
                          </p>
                        </div>
                      </div>

                      {formData.consumoLitros > 0 && esTipoAgua(item?.servicio?.tipo) && (
                        <div className="col-span-2">
                          <p className="text-sm text-muted-foreground">Consumo en Litros</p>
                          <p className="text-lg font-bold text-blue-700 dark:text-blue-400">
                            {formData.consumoLitros.toFixed(2)} L
                            {item?.servicio?.unidad === 'm3' && (
                              <span className="text-xs ml-2 text-muted-foreground font-normal">
                                (1 m³ = 1000 L)
                              </span>
                            )}
                            {item?.servicio?.unidad === 'galones' && (
                              <span className="text-xs ml-2 text-muted-foreground font-normal">
                                (1 gal = 3.785 L)
                              </span>
                            )}
                          </p>
                        </div>
                      )}

                      {exceedsLimit && (
                        <p className="text-sm text-red-600 dark:text-red-400 font-medium flex items-center gap-1">
                          <AlertTriangle className="h-4 w-4" />
                          Supera el límite diario configurado
                        </p>
                      )}
                      {nearLimit && (
                        <p className="text-sm text-amber-600 dark:text-amber-400 font-medium">
                          ⚠ Cerca del límite diario
                        </p>
                      )}
                      {errors.consumo && (
                        <p className="text-sm text-red-600 dark:text-red-400 font-medium">
                          {errors.consumo}
                        </p>
                      )}
                    </div>
                  )}
                </CardContent>

                {/* Footer con botones */}
                <div className="flex justify-end gap-3 p-6 border-t bg-muted/50">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={onClose}
                    disabled={isLoading}
                  >
                    Cancelar
                  </Button>
                  <Button
                    type="submit"
                    disabled={isLoading || Object.keys(errors).length > 0}
                  >
                    {isLoading ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
                        Guardando...
                      </>
                    ) : (
                      <>
                        <Save className="h-4 w-4 mr-2" />
                        Guardar Cambios
                      </>
                    )}
                  </Button>
                </div>
              </form>
            </Card>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};

export default EditUtilityServiceModal;
